import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { FAQS } from '../../mocks/sharedData';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  eyebrow?: string;
  headline?: string;
  items?: FAQItem[];
}

const FAQAccordion = ({
  eyebrow = 'Voice Search FAQ',
  headline = 'Common Questions About Solar in Kentuckiana',
  items = FAQS,
}: FAQAccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-stone-50 py-32" id="faq" aria-label="Frequently Asked Questions">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left — sticky header */}
          <div
            ref={ref}
            className={`lg:col-span-2 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-px bg-amber-500" />
                <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-zinc-900 leading-tight mb-5">
                {headline}
              </h2>
              <p className="text-zinc-500 text-base leading-relaxed mb-8">
                Real answers to the questions homeowners and businesses across Southern Indiana
                and North Central Kentucky ask most about solar energy.
              </p>

              {/* Feature image */}
              <div className="rounded-3xl overflow-hidden h-52">
                <img
                  src="https://public.readdy.ai/ai/img_res/d341c598e5aca582bd802f04598107a8.jpg"
                  alt="Solar panels on residential roof"
                  className="w-full h-full object-cover object-center"
                />
              </div>

              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <p className="text-zinc-700 text-sm leading-relaxed">
                  <strong>Still have questions?</strong> Call us at{' '}
                  <a href="tel:+18129130867" className="text-amber-600 font-bold cursor-pointer hover:text-amber-700">
                    812-913-0867
                  </a>{' '}
                  — Mon–Fri, 9 AM–5 PM.
                </p>
              </div>
            </div>
          </div>

          {/* Right — Accordion */}
          <div
            className={`lg:col-span-3 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="space-y-3">
              {items.map((item, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-2xl border overflow-hidden transition-all duration-200 ${
                    openIndex === i ? 'border-amber-200' : 'border-stone-100 hover:border-stone-200'
                  }`}
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    className="w-full flex items-start justify-between gap-4 px-7 py-6 text-left cursor-pointer hover:bg-stone-50 transition-colors duration-200"
                    aria-expanded={openIndex === i}
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-amber-500 font-bold text-sm flex-shrink-0 mt-0.5">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="font-semibold text-zinc-800 text-base leading-snug">
                        {item.question}
                      </span>
                    </div>
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 bg-stone-100 rounded-full mt-0.5">
                      <i
                        className={`text-amber-600 text-base transition-transform duration-300 ${
                          openIndex === i ? 'ri-subtract-line' : 'ri-add-line'
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === i ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-7 pb-6 pl-[4.5rem] border-t border-stone-100">
                      <p className="text-zinc-600 text-sm leading-relaxed pt-4">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Schema.org FAQ */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: items.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: { '@type': 'Answer', text: item.answer },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
};

export default FAQAccordion;
